import { ModelTypeEnum, QuestionnaireAnswerCreate, QuestionnaireApi, RiskMetric, TaskTypeEnum } from '@api';
import { getTabularModelClass } from '@helpers';
import { hasStyleMetric } from '@lib/styleMetric';
import { DataCollectionInputs, NewFile } from '@types';

const TEXT_GENERATION_METRIC_NAMES = {
  personalDataExposure: 'Personal Data Exposure',
  criteriaInsightfulness: 'Criteria Insightfulness',
  keyTakeawayScore: 'Key Takeaway Score',
} as const;

// Text generation inputs sent only if these metrics selected
function getTextGenerationFlags(selectedRiskMetrics: RiskMetric[]) {
  const names = selectedRiskMetrics.map(m => m.name);
  return {
    hasReportType:
      names.includes(TEXT_GENERATION_METRIC_NAMES.criteriaInsightfulness) ||
      names.includes(TEXT_GENERATION_METRIC_NAMES.keyTakeawayScore),
    hasPersonalDataExposure: names.includes(TEXT_GENERATION_METRIC_NAMES.personalDataExposure),
    hasInsightfulnessCriteria: names.includes(TEXT_GENERATION_METRIC_NAMES.criteriaInsightfulness),
  };
}

function getDistinctIndicators(selectedRiskMetrics: RiskMetric[]): string[] {
  return [...new Set(selectedRiskMetrics.map(m => m.indicator?.name).filter(Boolean))];
}

const mapDataCollectionInputsToQuestionnaireAnswer = (
  inputs: DataCollectionInputs,
  selectedRiskMetrics: RiskMetric[],
  protectedGroupDatasetId: number | null,
): Omit<QuestionnaireAnswerCreate, 'id'> => {
  const pipeline = inputs.dataPipeline;
  const { taskType } = inputs;
  const indicators = getDistinctIndicators(selectedRiskMetrics);

  const base: Omit<QuestionnaireAnswerCreate, 'id'> = {
    aiModel: inputs.modelId ?? pipeline.aiModelId,
    datasetPipeline: pipeline.id,
    performanceMetrics: [] as number[],
    selectedRiskMetrics: selectedRiskMetrics.map(m => m.id),
  };

  // Style spans several task types, so it goes on the shared base payload
  if (hasStyleMetric(selectedRiskMetrics) && inputs.styleCriteria?.length) {
    base.styleCriteria = inputs.styleCriteria;
  }

  if (taskType === TaskTypeEnum.tabularMl) {
    const hasPerformanceOrRobustness = indicators.some(name => name === 'performance' || name === 'robustness');
    return {
      ...base,
      performanceMetrics: hasPerformanceOrRobustness ? (inputs.performanceMetricRanked ?? []).map(Number) : [],
      modelType: (getTabularModelClass(inputs) as ModelTypeEnum | null) ?? null,
      ...(protectedGroupDatasetId != null && { protectedGroupDataset: protectedGroupDatasetId }),
    };
  }

  if (taskType === TaskTypeEnum.textGeneration) {
    const textGenerationFlags = getTextGenerationFlags(selectedRiskMetrics);
    const payload: Omit<QuestionnaireAnswerCreate, 'id'> = {
      ...base,
    };
    if (textGenerationFlags.hasReportType && inputs.reportType) {
      payload.reportType = inputs.reportType;
    }
    if (textGenerationFlags.hasPersonalDataExposure && inputs.textGenerationPersonalDataCategories?.length) {
      payload.personalDataExposureCategories = inputs.textGenerationPersonalDataCategories;
    }
    if (textGenerationFlags.hasInsightfulnessCriteria && inputs.textGenerationInsightfulnessCriteria?.length) {
      payload.insightfulnessCriteria = inputs.textGenerationInsightfulnessCriteria;
    }
    return payload;
  }

  return base;
};

// create protected group dataset only if fairness metrics selected
const createProtectedGroupDataset = async (questionnaireApi: QuestionnaireApi, inputs: DataCollectionInputs) => {
  if (!inputs.protectedGroupsFile || !inputs.protectedGroupsRanked?.length) {
    return null;
  }

  const isNewUpload = 'file' in inputs.protectedGroupsFile;

  // Reassessing with the same, previously-uploaded protected groups file: reuse the existing
  // dataset rather than re-uploading (the create endpoint requires a real file, not a path).
  if (!isNewUpload && inputs.existingProtectedGroupDatasetId) {
    return inputs.existingProtectedGroupDatasetId;
  }

  const result = await questionnaireApi.apiProtectedGroupDatasetsCreate({
    datasetPath: (inputs.protectedGroupsFile as NewFile).file,
    protectedGroups: inputs.protectedGroupsRanked.map(Number),
  });
  return result.id;
};

/**
 * Creates a new QuestionnaireAnswer via the API and returns its ID.
 */
export async function createQuestionnaireAnswer(
  questionnaireApi: QuestionnaireApi,
  inputs: DataCollectionInputs,
  selectedRiskMetrics: RiskMetric[],
): Promise<number> {
  const indicators = getDistinctIndicators(selectedRiskMetrics);

  const protectedGroupDatasetId =
    inputs.taskType === TaskTypeEnum.tabularMl && indicators.includes('fairness')
      ? await createProtectedGroupDataset(questionnaireApi, inputs)
      : null;

  const questionnaireAnswerCreate = mapDataCollectionInputsToQuestionnaireAnswer(
    inputs,
    selectedRiskMetrics,
    protectedGroupDatasetId,
  );

  const result = await questionnaireApi.apiQuestionnaireAnswersCreate({ questionnaireAnswerCreate });
  return result.id;
}

export async function updateQuestionnaireAnswer(
  questionnaireApi: QuestionnaireApi,
  inputs: DataCollectionInputs,
  selectedRiskMetrics: RiskMetric[],
): Promise<number> {
  const indicators = getDistinctIndicators(selectedRiskMetrics);
  const protectedGroupDatasetId =
    inputs.taskType === TaskTypeEnum.tabularMl && indicators.includes('fairness')
      ? await createProtectedGroupDataset(questionnaireApi, inputs)
      : null;

  const questionnaireAnswerCreate = mapDataCollectionInputsToQuestionnaireAnswer(
    inputs,
    selectedRiskMetrics,
    protectedGroupDatasetId,
  );

  const result = await questionnaireApi.apiQuestionnaireAnswersUpdate({ id: inputs.id!, questionnaireAnswerCreate });
  return result.id;
}
